import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type SyntheticEvent, type ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Notice } from './login-view';

/** Button type that submits the form; a constant because the i18n lint rule flags string attributes. */
const SUBMIT = 'submit';

/** Props of {@link CodeStep}. */
export interface CodeStepProps {
  /** The account the code went to, masked by the API (R12): shown as text, never parsed. */
  readonly phoneMasked: string;
  /** How many digits Telegram sent. */
  readonly codeLength: number;
  /** The failure to show, used here only to put the cursor back in the empty field. */
  readonly notice: Notice | null;
  readonly pending: boolean;
  /** True while Telegram's delay runs (R17). */
  readonly newCodeDisabled: boolean;
  /** The digits, passed once; the field is emptied right after (R19). */
  readonly onSubmit: (code: string) => void;
  readonly onNewCode: () => void;
}

/**
 * Code step of E2 (specs/S4-telegram-login/spec.md R15, R19): one numeric one-time-code field of
 * `codeLength` digits (Proposal P6), the warning that a Telegram password may follow, Confirm and
 * "Get a new code". It asks for no username and no new password.
 */
export function CodeStep({
  phoneMasked,
  codeLength,
  notice,
  pending,
  newCodeDisabled,
  onSubmit,
  onNewCode,
}: CodeStepProps): ReactElement {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const field = useRef<HTMLInputElement>(null);
  // S4 R17: focus the empty field on arrival and after each failure (focus moves, a11y AC36).
  useEffect(() => {
    field.current?.focus();
  }, [notice]);

  function submit(event: SyntheticEvent): void {
    event.preventDefault();
    if (pending || value.length !== codeLength) return;
    const code = value;
    // R19, I9: the code leaves the field the moment it is handed over, success or not.
    setValue('');
    onSubmit(code);
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Typography
        component="h1"
        sx={{ fontSize: 26, lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 700 }}
      >
        {t('onboarding.telegram.code.title')}
      </Typography>
      <Typography sx={{ lineHeight: 1.5 }}>
        {t('onboarding.telegram.code.intro', { length: codeLength, phone: phoneMasked })}
      </Typography>
      <Box
        component="form"
        noValidate
        onSubmit={submit}
        sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
      >
        <TextField
          label={t('onboarding.telegram.code.label')}
          value={value}
          onChange={(event) => {
            setValue(event.target.value.replaceAll(/\D/g, '').slice(0, codeLength));
          }}
          inputRef={field}
          fullWidth
          slotProps={{
            htmlInput: {
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              maxLength: codeLength,
              pattern: '[0-9]*',
            },
          }}
        />
        <Box
          sx={(theme) => ({
            bgcolor: theme.fx.infoBg,
            borderRadius: 2,
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          })}
        >
          <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
            {t('onboarding.telegram.code.twoFactorTitle')}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.45 }}>
            {t('onboarding.telegram.code.twoFactorBody')}
          </Typography>
        </Box>
        <Button
          type={SUBMIT}
          variant="contained"
          size="large"
          fullWidth
          disabled={pending || value.length !== codeLength}
          sx={{ minHeight: 52 }}
        >
          {t('onboarding.telegram.code.confirm')}
        </Button>
        <Button
          variant="text"
          onClick={onNewCode}
          disabled={pending || newCodeDisabled}
          sx={{ minHeight: 44 }}
        >
          {t('onboarding.telegram.code.newCode')}
        </Button>
      </Box>
    </Box>
  );
}
